import {useEffect, useState} from "react";
import {ShapeController} from "../../controller/ShapeController";

export const lineWidth = 3;
// Lighten a hex color by a given factor (0-1, where 1 = white)
export const lightenColor = (hex: string, factor: number = 0.8): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    const newR = Math.round(r + (255 - r) * factor);
    const newG = Math.round(g + (255 - g) * factor);
    const newB = Math.round(b + (255 - b) * factor);

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};



/**
 * Custom hook that encapsulates common shape effects and state management
 */
export function useShapeEffects(
    controller: ShapeController,
    touchingObject: boolean,
    color: string,
    positionZ: number,
    minZ: number
) {

    const calcIntensity = (z: number) => (1-z) * 0.9;
    const calcMetalness = (z: number) => 0.1 + (1-z) * 0.5;
    const calcRoughness = (z: number) => 0.6 - (1-z) * 0.3;

    const [edgeColor, setEdgeColor] = useState<string | null>(null);
    const [emissiveIntensity, setEmissiveIntensity] = useState(calcIntensity(positionZ));
    const [metalness, setMetalness] = useState(calcMetalness(positionZ));
    const [roughness, setRoughness] = useState(calcRoughness(positionZ));


    // Dispose controller on unmount
    useEffect(() => {
        return () => {
            controller.dispose();
        };
    }, [controller]);

    // Update edge color when touching object
    useEffect(() => {
        setEdgeColor(touchingObject ? lightenColor(color) : null);
    }, [touchingObject, color]);

    // Update material properties based on z-position
    useEffect(() => {
        setEmissiveIntensity(calcIntensity(positionZ/minZ));
        setMetalness(calcMetalness(positionZ/minZ));
        setRoughness(calcRoughness(positionZ/minZ));
    }, [positionZ]);

    return {
        edgeColor,
        emissiveIntensity,
        metalness,
        roughness,
        opacity: 0.8
    };
}
